import React, { useEffect, useState, useRef } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import { Navigate, useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * NOTE FOR PRODUCTION:
 * To use this in your local project, uncomment your original imports 
 * and remove the internal mock implementations below.
 */

import { useAuth } from "../hooks/useAuth";
import Logo from "./homepage/images/logo.png";
import { API_BY_COUNTRY } from "../hooks/commanFunc";
import { updateApiBaseUrl } from "../hooks/apiHandler";


// // --- INTERNAL MOCK IMPLEMENTATIONS (To resolve compilation errors in Canvas) ---
// const useAuth = () => ({
//   login: async ({ email, password }) => {
//     console.log("Step 1: Requesting OTP for:", email);
//     // Simulating the backend response: { code: 0, data: { step: 'verification_required' } }
//     return { code: 0, data: { step: 'verification_required', email } };
//   },
//   user: null,
//   verifyOTP: async ({ email, otp }) => {
//     console.log("Step 2: Verifying OTP:", otp, "for", email);
//     // Simulating JWT response
//     return { code: 0, msg: 'Welcome' };
//   }
// });




export const LoginPage = () => {
  const { login, user, verifyOTP } = useAuth();
  const navigate = useNavigate();
  
  // --- STATE ---
  const [step, setStep] = useState(1); // 1: Login, 2: OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [region, setRegion] = useState("Canada");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  
  // OTP State
  const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
  const otpRefs = useRef([]);

  // --- AUTO-FOCUS FIRST OTP FIELD WHEN STEP CHANGES TO 2 ---
  useEffect(() => {
    if (step === 2 && otpRefs.current[0]) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  // --- REGION DETECTION ---
  useEffect(() => {
    let detectedRegion = window.localStorage.getItem("api_region");
    if (detectedRegion) {
      setRegion(detectedRegion);
    } else {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      detectedRegion = timeZone.startsWith("Asia/") ? "India" : "Canada";
      setRegion(detectedRegion);
    }
    const newUrl = API_BY_COUNTRY[detectedRegion] || API_BY_COUNTRY["Canada"];
    updateApiBaseUrl(newUrl, detectedRegion);
  }, []);

  // --- HANDLERS ---

  const handleRegionChange = (event) => {
    const selectedRegion = event.target.value;
    const newUrl = API_BY_COUNTRY[selectedRegion];
    setRegion(selectedRegion);
    updateApiBaseUrl(newUrl, selectedRegion);
    window.localStorage.setItem("api_region", selectedRegion);
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (!agreedToTerms) return;
    
    setError("");
    setLoading(true);

    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    if (!email || !password) {
      setError("Please fill in all fields.");
      setLoading(false);
      return;
    }

    try {
      // Step 1: POST to /login (Triggers OTP generation)
      let resp = await login({ email, password });
      console.log(resp);
      // Checking for your backend response code 0 and the verification step
      if (resp && resp.code === 0 && resp.data?.step === 'verification_required') {
        setLoginData({ email });
        setStep(2);
      } else if (resp && resp.msg) {
        setError(resp.msg);
      } else {
        setError("Invalid email or password. Please try again.");
      }
    } catch (err) {
      setError("An error occurred during login. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (isAutoSubmit = false) => {
    const otpCode = otpArray.join("");
    
    // Only show validation error on manual button click, not on auto-submit
    if (otpCode.length < 6) {
      if (!isAutoSubmit) {
        return setError("Please enter the full 6-digit code");
      }
      return; // Silent return on auto-submit if incomplete
    }

    setLoading(true);
    setError("");

    try {
      // Step 2: POST to /verify-otp
      // sending 'otp' as key to match your backend's req.body.otp
      let resp = await verifyOTP({ email: loginData.email, otp: otpCode, type:"login" });
      
      if (resp && resp.code === 0) {
        navigate("/store");
      } else if (resp && resp.msg) {
        setError(resp.msg);
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } catch (err) {
      setError("Verification failed. Please check your network.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    const value = element.value;
    
    // Only allow numbers (0-9)
    if (value && (isNaN(value) || !/^[0-9]$/.test(value))) {
      return false;
    }
    
    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtpArray(newOtp);

    // Auto-focus next field
    if (value !== "" && index < 5) {
      otpRefs.current[index + 1].focus();
    }
    
    // Auto-submit when all 6 digits are entered
    if (value !== "" && index === 5) {
      const fullOtp = [...newOtp];
      if (fullOtp.every(digit => digit !== "")) {
        // Small delay to allow state update, mark as auto-submit
        setTimeout(() => handleVerifyOTP(true), 100);
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
    
    // Handle Enter key - trigger verification
    if (e.key === "Enter") {
      e.preventDefault();
      const otpCode = otpArray.join("");
      if (otpCode.length === 6) {
        handleVerifyOTP();
      }
    }
    
    // Handle left/right arrow navigation
    if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1].focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted data is exactly 6 digits
    if (/^[0-9]{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtpArray(newOtp);
      
      // Focus the last input
      otpRefs.current[5].focus();
      
      // Auto-submit after paste, mark as auto-submit
      setTimeout(() => handleVerifyOTP(true), 100);
    }
  };

  // --- RENDERING ---

  if (user) {
    return <Navigate to="/store" replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fdfdfd' }}>
      <ArrowBackIcon 
        sx={{ 
          color: '#222', 
          marginLeft: '25px', 
          marginTop: '25px', 
          borderRadius: '100%', 
          border: '1px solid #222', 
          fontSize: '32px',
          cursor: 'pointer',
          '&:hover': { bgcolor: '#eee' }
        }}
        onClick={() => step === 2 ? setStep(1) : navigate("/")}
      />

      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <img 
            src={Logo} 
            alt="miirapos_logo" 
            width='120px' 
            style={{ cursor: 'pointer', marginBottom: '20px' }} 
            onClick={() => navigate("/")} 
          />

          <Typography component="h1" variant="h5" fontWeight="bold">
            {step === 1 ? "Log In" : "Security Verification"}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%', borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {step === 1 ? (
            /* --- LOGIN FORM --- */
            <Box component="form" onSubmit={handleLoginSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="region-label">Region</InputLabel>
                <Select
                  labelId="region-label"
                  value={region}
                  label="Region"
                  onChange={handleRegionChange}
                  size="small"
                >
                  {Object.keys(API_BY_COUNTRY).map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                variant="outlined"
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                variant="outlined"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    value="agree"
                    color="primary"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the&nbsp;
                    <Link href="/terms" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      Terms & Conditions
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!agreedToTerms || loading}
                sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Log In"}
              </Button>
              <br />
              
              <Grid container justifyContent="center">
                <Grid item textAlign={"center"}>
                  <Link href="/register" variant="body2" sx={{ textDecoration: 'none', fontWeight: 'bold' }}>
                    {"Don't have an account? Sign Up"}
                  </Link>
                  <br/>
                  <br/>
                   <Link href="/reset-password" variant="body2" sx={{ textDecoration: 'none', fontWeight: 'bold' }}>
                    {"Forgot your password?"}
                  </Link>
                </Grid> 
              </Grid>
            </Box>
          ) : (
            /* --- OTP VERIFICATION VIEW --- */
            <Box sx={{ mt: 4, width: '100%', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                We've sent a 6-digit verification code to <br />
                <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
                  {loginData.email}
                </Typography>
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 4 }}>
                {otpArray.map((data, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (otpRefs.current[index] = el)}
                    value={data}
                    onChange={(e) => handleOtpChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    inputProps={{ 
                      maxLength: 1,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        fontSize: '1.2rem', 
                        padding: '12px 0' 
                      } 
                    }}
                    sx={{ width: '48px' }}
                    variant="outlined"
                  />
                ))}
              </Stack>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerifyOTP}
                disabled={loading}
                sx={{ py: 1.5, fontWeight: 'bold', borderRadius: 2, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Verify & Log In"}
              </Button>

              <Button 
                fullWidth 
                variant="text" 
                onClick={() => {
                  setStep(1);
                  setOtpArray(new Array(6).fill(""));
                }}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 'bold' }}
              >
                Back to Login
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;